import { supabase } from '../supabaseClient';
import {
  summarisePatrolRouteCoverage,
  type PatrolRouteCoverage,
} from '../utils/patrolRoute';

export interface PatrolCheckpointQr {
  name: string;
  tokenId: string | null;
}

/** A recorded leg as returned by the get_patrol_route_legs RPC. */
export interface PatrolRouteLegRow {
  patrol_id: string;
  from_checkpoint_id: string;
  to_checkpoint_id: string;
  is_closing: boolean;
  /** Precision-5 encoded polyline. */
  polyline: string;
  distance_m: number;
  recorded_at: string;
}

export interface CreatePatrolCheckpointInput {
  checkpointId?: string;
  label: string;
  checkpointName: string;
  latitude: number;
  longitude: number;
  qrRequired: boolean;
  code?: string | null;
  geofenceRadius?: number | null;
}

export interface CreatePatrolParams {
  name: string;
  siteId: string;
  durationMinutes?: number | null;
  description?: string;
  isLoop?: boolean;
  checkpoints: CreatePatrolCheckpointInput[];
}

interface CreatedPatrol {
  id: string;
}

export const patrolService = {
  async getCurrentUserCompanyId(): Promise<string> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', user.id)
      .single();

    if (error) throw error;
    return (data as { company_id: string }).company_id;
  },

  async fetchSites() {
    const companyId = await this.getCurrentUserCompanyId();
    const { data, error } = await supabase
      .from('sites')
      .select(`
        *,
        client_contract:client_contracts!sites_client_contract_id_fkey(name, status)
      `)
      .eq('company_id', companyId)
      .order('name', { ascending: true });

    if (error) throw error;
    const rows = (data ?? []) as Array<Record<string, unknown> & {
      client_contract?: { status?: string | null } | Array<{ status?: string | null }> | null;
    }>;
    return rows.filter((site) => {
      const clientContract = Array.isArray(site.client_contract) ? site.client_contract[0] : site.client_contract;
      return clientContract?.status !== 'archived';
    });
  },

  async fetchCheckpointsBySite(siteId: string) {
    const { data, error } = await supabase
      .from('checkpoints')
      .select('*')
      .eq('site_id', siteId)
      .order('name', { ascending: true });

    if (error) throw error;
    return data ?? [];
  },

  async createPatrol({
    name,
    siteId,
    durationMinutes = null,
    description,
    isLoop = false,
    checkpoints,
  }: CreatePatrolParams) {
    const companyId = await this.getCurrentUserCompanyId();

    const { data: patrol, error: patrolError } = await supabase
      .from('patrols')
      .insert({
        name,
        site_id: siteId,
        estimated_duration_minutes: durationMinutes,
        description: description || null,
        company_id: companyId,
        is_loop: isLoop,
      })
      .select('id')
      .single();

    if (patrolError) throw patrolError;

    const patrolRow = patrol as CreatedPatrol;
    const patrolCheckpoints: Array<{
      patrol_id: string;
      checkpoint_id: string;
      sequence_order: number;
      is_required: boolean;
      label: string;
    }> = [];

    for (let index = 0; index < checkpoints.length; index += 1) {
      const checkpoint = checkpoints[index];
      let checkpointId = checkpoint.checkpointId;

      if (!checkpointId) {
        const { data: createdCheckpoint, error: checkpointError } = await supabase
          .from('checkpoints')
          .insert({
            name: checkpoint.checkpointName || checkpoint.label,
            code: checkpoint.code || null,
            description: null,
            site_id: siteId,
            geofence_radius: checkpoint.geofenceRadius ?? 50,
            location: `SRID=4326;POINT(${checkpoint.longitude} ${checkpoint.latitude})`,
          })
          .select('id')
          .single();

        if (checkpointError) throw checkpointError;
        checkpointId = (createdCheckpoint as { id: string }).id;
      } else {
        const { error: checkpointUpdateError } = await supabase
          .from('checkpoints')
          .update({
            name: checkpoint.checkpointName || checkpoint.label,
            code: checkpoint.code || null,
            geofence_radius: checkpoint.geofenceRadius ?? 50,
            location: `SRID=4326;POINT(${checkpoint.longitude} ${checkpoint.latitude})`,
          })
          .eq('id', checkpointId);

        if (checkpointUpdateError) throw checkpointUpdateError;
      }

      patrolCheckpoints.push({
        patrol_id: patrolRow.id,
        checkpoint_id: checkpointId,
        sequence_order: index + 1,
        is_required: checkpoint.qrRequired,
        label: checkpoint.label,
      });
    }

    if (patrolCheckpoints.length > 0) {
      const { error: checkpointInsertError } = await supabase
        .from('patrol_checkpoints')
        .insert(patrolCheckpoints);

      if (checkpointInsertError) throw checkpointInsertError;
    }

    return patrolRow;
  },

  async fetchPatrols() {
    const companyId = await this.getCurrentUserCompanyId();
    const { data, error } = await supabase
      .from('patrols')
      .select('*, sites(name)')
      .eq('company_id', companyId)
      .order('name', { ascending: true });

    if (error) throw error;
    return data ?? [];
  },

  async fetchPatrolsBySite(siteId: string) {
    const { data, error } = await supabase
      .from('patrols')
      .select('*, sites(name)')
      .eq('site_id', siteId)
      .order('name', { ascending: true });

    if (error) throw error;
    return data ?? [];
  },

  async fetchPatrolCheckpoints(patrolId: string) {
    const { data, error } = await supabase
      .from('patrol_checkpoints')
      .select('id, patrol_id, checkpoint_id, sequence_order, is_required, label, checkpoints(*)')
      .eq('patrol_id', patrolId)
      .order('sequence_order', { ascending: true });

    if (error) throw error;
    return data ?? [];
  },

  async fetchCheckpointQrCodes(patrolId: string): Promise<PatrolCheckpointQr[]> {
    const { data: patrolCps, error: patrolCheckpointError } = await supabase
      .from('patrol_checkpoints')
      .select('sequence_order, checkpoint_id, is_required, label, checkpoints(name)')
      .eq('patrol_id', patrolId)
      .eq('is_required', true)
      .order('sequence_order', { ascending: true });

    if (patrolCheckpointError) throw patrolCheckpointError;
    if (!patrolCps || patrolCps.length === 0) return [];

    const checkpointIds = (patrolCps as Array<{ checkpoint_id: string }>).map((pc) => pc.checkpoint_id);

    const { data: qrCodes, error: qrError } = await supabase
      .from('checkpoint_qr_codes')
      .select('checkpoint_id, token_id')
      .in('checkpoint_id', checkpointIds);

    if (qrError) throw qrError;

    const qrMap: Record<string, string> = {};
    for (const qr of (qrCodes ?? []) as Array<{ checkpoint_id: string; token_id: string }>) {
      qrMap[qr.checkpoint_id] = qr.token_id;
    }

    return (patrolCps as Array<{
      checkpoint_id: string;
      label?: string | null;
      checkpoints?: { name?: string | null } | null;
    }>).map((pc) => ({
      name: pc.label || pc.checkpoints?.name || 'Unknown checkpoint',
      tokenId: qrMap[pc.checkpoint_id] ?? null,
    }));
  },

  async updatePatrol(patrolId: string, {
    name,
    siteId,
    durationMinutes = null,
    description,
    isLoop = false,
    checkpoints,
  }: CreatePatrolParams): Promise<void> {
    const { error: patrolError } = await supabase
      .from('patrols')
      .update({
        name,
        site_id: siteId,
        estimated_duration_minutes: durationMinutes,
        description: description || null,
        is_loop: isLoop,
      })
      .eq('id', patrolId);

    if (patrolError) throw patrolError;

    const { error: deleteError } = await supabase
      .from('patrol_checkpoints')
      .delete()
      .eq('patrol_id', patrolId);

    if (deleteError) throw deleteError;

    const patrolCheckpoints: Array<{
      patrol_id: string;
      checkpoint_id: string;
      sequence_order: number;
      is_required: boolean;
      label: string;
    }> = [];

    for (let index = 0; index < checkpoints.length; index += 1) {
      const checkpoint = checkpoints[index];
      let checkpointId = checkpoint.checkpointId;

      if (!checkpointId) {
        const { data: createdCheckpoint, error: checkpointError } = await supabase
          .from('checkpoints')
          .insert({
            name: checkpoint.checkpointName || checkpoint.label,
            code: checkpoint.code || null,
            description: null,
            site_id: siteId,
            geofence_radius: checkpoint.geofenceRadius ?? 50,
            location: `SRID=4326;POINT(${checkpoint.longitude} ${checkpoint.latitude})`,
          })
          .select('id')
          .single();

        if (checkpointError) throw checkpointError;
        checkpointId = (createdCheckpoint as { id: string }).id;
      } else {
        const { error: checkpointUpdateError } = await supabase
          .from('checkpoints')
          .update({
            name: checkpoint.checkpointName || checkpoint.label,
            code: checkpoint.code || null,
            geofence_radius: checkpoint.geofenceRadius ?? 50,
            location: `SRID=4326;POINT(${checkpoint.longitude} ${checkpoint.latitude})`,
          })
          .eq('id', checkpointId);

        if (checkpointUpdateError) throw checkpointUpdateError;
      }

      patrolCheckpoints.push({
        patrol_id: patrolId,
        checkpoint_id: checkpointId,
        sequence_order: index + 1,
        is_required: checkpoint.qrRequired,
        label: checkpoint.label,
      });
    }

    if (patrolCheckpoints.length > 0) {
      const { error: insertError } = await supabase
        .from('patrol_checkpoints')
        .insert(patrolCheckpoints);

      if (insertError) throw insertError;
    }
  },

  /**
   * Recorded route legs for the given patrols: paths guards walked between
   * consecutive QR checkpoints. The database returns only legs that still fit
   * each patrol as it is defined now.
   */
  async fetchPatrolRouteLegs(patrolIds: string[]): Promise<PatrolRouteLegRow[]> {
    if (patrolIds.length === 0) return [];

    const { data, error } = await supabase.rpc('get_patrol_route_legs', {
      p_patrol_ids: patrolIds,
    });

    if (error) throw error;
    return (data ?? []) as PatrolRouteLegRow[];
  },

  /** How much of each patrol's route has been recorded, and whether recording is on. */
  async fetchPatrolRouteCoverage(patrolIds: string[]): Promise<Record<string, PatrolRouteCoverage>> {
    if (patrolIds.length === 0) return {};

    const [legs, requiredResult, settingsResult] = await Promise.all([
      this.fetchPatrolRouteLegs(patrolIds),
      supabase
        .from('patrol_checkpoints')
        .select('patrol_id')
        .in('patrol_id', patrolIds)
        .eq('is_required', true),
      supabase
        .from('patrol_route_settings')
        .select('patrol_id, recording_enabled')
        .in('patrol_id', patrolIds),
    ]);

    if (requiredResult.error) throw requiredResult.error;
    if (settingsResult.error) throw settingsResult.error;

    return summarisePatrolRouteCoverage(
      patrolIds,
      (requiredResult.data ?? []) as Array<{ patrol_id: string }>,
      legs,
      (settingsResult.data ?? []) as Array<{ patrol_id: string; recording_enabled: boolean }>,
    );
  },

  /**
   * Turns route recording off or on for a patrol. Off deletes its recorded
   * legs; on counts only walks from that moment.
   */
  async setPatrolRouteRecording(patrolId: string, enabled: boolean): Promise<void> {
    const { error } = await supabase.rpc('set_patrol_route_recording', {
      p_patrol_id: patrolId,
      p_enabled: enabled,
    });

    if (error) throw error;
  },

  async deletePatrol(patrolId: string): Promise<void> {
    // Get any patrol_runs so we can clean up their children first
    const { data: runs } = await supabase
      .from('patrol_runs')
      .select('id')
      .eq('patrol_id', patrolId);

    const runIds = (runs ?? []).map((r: { id: string }) => r.id);

    if (runIds.length > 0) {
      // Delete checkpoint events referencing these runs (no cascade on FK)
      const { error: eventsError } = await supabase
        .from('patrol_checkpoint_events')
        .delete()
        .in('patrol_run_id', runIds);
      if (eventsError) throw eventsError;

      // Delete patrol_runs (cascades patrol_run_checkpoints & patrol_run_locations)
      const { error: runsError } = await supabase
        .from('patrol_runs')
        .delete()
        .eq('patrol_id', patrolId);
      if (runsError) throw runsError;
    }

    // Delete patrol_checkpoints (no cascade on FK)
    const { error: checkpointsError } = await supabase
      .from('patrol_checkpoints')
      .delete()
      .eq('patrol_id', patrolId);
    if (checkpointsError) throw checkpointsError;

    // Delete the patrol (cascades patrol_route_cache)
    const { error } = await supabase
      .from('patrols')
      .delete()
      .eq('id', patrolId);
    if (error) throw error;
  },
};
